import React, { useCallback, useMemo } from 'react';
import classnames from 'clsx';
import { useIntl } from 'react-intl';

import { NumberInput } from 'ui-components/components/number-input';
import { InputAdornmentPosition } from 'ui-components/components/number-input/input-adornment-position';
import { Text, TextType } from 'ui-components/components/text';

import { messages } from './messages';
import styles from './plan-user-count.module.css';
import planDetailStyles from '../plan-details.module.css';
import { UserCost } from './user-cost';
import { SectionIds } from '../../../manage-subscription-drawer/constants';
import { useManageSubscriptionDrawerContext } from '../../../../context/manage-subscription-drawer-context';
import { Features } from '../../../../../../../constants/features';
import { useGetNumberOfIncludedUserSeatsForPlan } from '../../../../hooks/use-plan-details';
import { isFeatureIncludedInBillingPlan } from '../../../../utils/plan-utilities';

/**
 * @param {object} props - Component props
 * @param {number} [props.discountPercent] - Effective discount percentage to apply
 * @param {(billingPlanId: string, count: number) => void} props.onChange - Function to handle user count change
 * @param {BillingPlan} props.plan - The plan data
 * @param {number} props.additionalUserCount - The additional user count for this plan
 * @returns {React.JSX.Element} The rendered component
 */
export const PlanUserCount = ({ discountPercent, onChange, plan, additionalUserCount }) => {
  const canAddAdditionalUsers = useMemo(() => {
    return isFeatureIncludedInBillingPlan(plan, Features.PurchaseAdditionalUsers);
  }, [plan]);

  const { highlightSection } = useManageSubscriptionDrawerContext();
  const shouldHighlight = highlightSection === SectionIds.Users;

  const includedUserCount = useGetNumberOfIncludedUserSeatsForPlan(plan.billingPlanId);
  const intl = useIntl();
  // Using intl.formatNumber instead of <FormattedMessage> because the message that uses this uses the {plural} modifier
  const formattedIncludedUserCount = intl.formatNumber(includedUserCount);

  const onChangeUserCount = useCallback(
    (count) => {
      const newAdditionalUserCount = count - includedUserCount;
      onChange(plan.billingPlanId, newAdditionalUserCount);
    },
    [includedUserCount, plan.billingPlanId, onChange],
  );

  if (!canAddAdditionalUsers) {
    return (
      <div className={classnames(styles['plan-user-count'], { [planDetailStyles.highlight]: shouldHighlight })}>
        <Text
          className={styles['read-only-users']}
          formattedMessageValues={{ count: formattedIncludedUserCount }}
          textType={TextType.Paragraph}
          value={messages.usersCount}
        />
      </div>
    );
  }

  return (
    <div className={classnames(styles['plan-user-count'], { [planDetailStyles.highlight]: shouldHighlight })}>
      <Text className={styles['user-label']} textType={TextType.Label} value={messages.selectUsersLabel} />
      <NumberInput
        adornment={<UserCost additionalUsers={additionalUserCount} billingPlanId={plan.billingPlanId} discountPercent={discountPercent} />}
        adornmentPosition={InputAdornmentPosition.End}
        className={styles['user-input']}
        label={intl.formatMessage(messages.selectUsersLabel)}
        min={includedUserCount}
        onChange={onChangeUserCount}
        value={includedUserCount + additionalUserCount}
      />
    </div>
  );
};
